import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Input, Modal, Typography, Form } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { SceneItemType, SceneVoiceoverItem, Word } from '@monorepo/types';
import { useVideoContext } from '../../../../hooks/use-video';
import { useNotification } from '../../../../hooks/use-notification';
import { useStore } from '../../../../helpers/use-store';
import { useTranslation } from 'react-i18next';

export type WordWithMetadata = Word & {
  sceneId: string;
};

const { Text } = Typography;

export interface EditWordModalActions {
  open: (word: WordWithMetadata) => void;
}

const EditWordModal = forwardRef<EditWordModalActions>((_, ref) => {
  const { t } = useTranslation();
  const { scenes, updateSceneWords } = useVideoContext();
  const [isOpen, setIsOpen] = useState(false);
  const [currentWord, setCurrentWord] = useState<WordWithMetadata | null>(null);
  const [newWordText, setNewWordText] = useState('');
  const { loadingState, updateLoadingState } = useLoading();
  const notificationApi = useNotification();
  const {
    dataStore: { videoStore },
  } = useStore();

  useImperativeHandle(ref, () => ({
    open: (word: WordWithMetadata) => {
      setCurrentWord(word);
      setNewWordText(word.word);
      setIsOpen(true);
    },
  }));

  const handleCancel = () => {
    setIsOpen(false);
    setCurrentWord(null);
    setNewWordText('');
  };

  const handleSave = async () => {
    if (!currentWord) return;

    try {
      updateLoadingState(LoadingState.Loading);

      const newWord = {
        word: newWordText,
        start: currentWord.start,
        end: currentWord.end,
      };

      const foundScene = scenes.find(
        (scene) => scene._id === currentWord.sceneId
      );

      if (!foundScene) {
        throw new Error(`Scene: ${currentWord.sceneId} not found here!`);
      }

      const sceneVoiceoverItem = foundScene.items.find(
        (sceneItem) => sceneItem.type === SceneItemType.Voiceover
      );
      const sceneVoiceoverData =
        (sceneVoiceoverItem?.data as SceneVoiceoverItem) || { words: [] };

      const wordIndex = sceneVoiceoverData.words.findIndex(
        (sceneWord) =>
          sceneWord.start === newWord.start && sceneWord.end === newWord.end
      );

      if (wordIndex === -1) {
        throw new Error(`can't find wordIndex for: ${JSON.stringify(newWord)}`);
      }

      sceneVoiceoverData.words[wordIndex] = newWord;

      await updateSceneWords({
        sceneId: foundScene._id,
        videoId: foundScene.videoId,
        words: sceneVoiceoverData.words,
      });

      notificationApi.success({
        placement: 'top',
        message: t('videoEditor.editWord.updateSuccess'),
        description: t('videoEditor.editWord.updateSuccessDescription', {
          from: currentWord.word,
          to: newWordText,
        }),
      });

      handleCancel();
    } catch (error) {
      console.error('Failed to update word:', error);
      notificationApi.error({
        placement: 'top',
        message: t('videoEditor.editWord.updateError'),
        description: t('videoEditor.editWord.updateErrorDescription'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  if (!currentWord) {
    return null;
  }

  const formattedTime = `${currentWord.start.toFixed(
    2
  )}s - ${currentWord.end.toFixed(2)}s`;

  return (
    <Modal
      title={
        <Typography.Title style={{ margin: 0 }} level={5}>
          {t('videoEditor.editWord.title')}{' '}
          <Text type="secondary" style={{ marginLeft: 8 }}>
            ({formattedTime})
          </Text>
        </Typography.Title>
      }
      open={isOpen}
      onCancel={(e) => {
        e.stopPropagation();

        handleCancel();
      }}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={loadingState === LoadingState.Loading}
        >
          {t('common.saveChanges')}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item
          label={
            <div>
              <div></div>
              <Typography.Text type={'secondary'}>
                {t('videoEditor.editWord.note')}
              </Typography.Text>
            </div>
          }
        >
          <Input
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.preventDefault();
              }
            }}
            value={newWordText}
            onChange={(e) => setNewWordText(e.target.value)}
            placeholder={t('videoEditor.editWord.placeholder')}
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default EditWordModal;
